/**
 * Skin quiz (sections/skin-quiz.liquid).
 *
 * Steps: 0 sign-up → 1 age → 2 skin type → 3 concern → 4 water → loading →
 * result. The routine is matched in the browser (SkinQuizMatcher, loaded
 * before this file); the lead and the entry are sent to the Matat app through
 * the app proxy without waiting for a response.
 */
(() => {
  if (customElements.get('skin-quiz')) return;

  const STAGES = ['cleansing', 'nourishing', 'treatment'];
  const LAST_QUESTION_STEP = 4;
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const READ_MORE_HEIGHT = { desktop: 85, mobile: 70 };

  const parseJSON = (element, fallback) => {
    try {
      return element ? JSON.parse(element.textContent) : fallback;
    } catch (error) {
      console.error('[skin-quiz] invalid JSON', error);
      return fallback;
    }
  };

  class SkinQuiz extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.config = parseJSON(this.querySelector('[data-skin-quiz-config]'), {});
      this.strings = this.config.strings || {};
      this.scenarios = parseJSON(this.querySelector('[data-skin-quiz-scenarios]'), []);
      this.steps = Array.from(this.querySelectorAll('.sq-step'));
      this.loading = this.querySelector('.sq-loading');
      this.result = this.querySelector('.sq-result');
      this.lead = null;
      this.answers = {};

      this.sortOptions();
      this.bindForm();
      this.bindQuestions();
      this.bindTooltips();
      this.bindResult();
      if (!this.restoreResult()) this.track('skin_quiz_start');
    }

    // ------------------------------------------------------------ navigation

    showStep(step) {
      this.steps.forEach((element) => {
        element.hidden = Number(element.dataset.step) !== step;
      });
      this.currentStep = step;

      const current = this.stepElement(step);
      const heading = current && current.querySelector('[tabindex="-1"]');
      this.scrollIntoViewIfNeeded();
      if (heading) heading.focus({ preventScroll: true });

      if (step > 0) this.track('skin_quiz_step', { skin_quiz_step: step });
    }

    stepElement(step) {
      return this.steps.find((element) => Number(element.dataset.step) === step);
    }

    scrollIntoViewIfNeeded() {
      if (this.getBoundingClientRect().top < 0) {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    /** Answers without an explicit theme-editor list are ordered by their sort_order field. */
    sortOptions() {
      this.querySelectorAll('[data-sort-options]').forEach((container) => {
        Array.from(container.children)
          .map((element, index) => ({ element, index, sort: Number(element.dataset.sort) || 0 }))
          .sort((a, b) => a.sort - b.sort || a.index - b.index)
          .forEach(({ element }) => container.appendChild(element));
      });
    }

    // ------------------------------------------------------------ step 0

    bindForm() {
      const form = this.querySelector('.sq-form');
      if (!form) return;

      form.addEventListener('submit', (event) => {
        event.preventDefault();

        const name = form.elements.full_name;
        const email = form.elements.email;
        const nameValue = name.value.trim();
        const emailValue = email.value.trim();

        const nameError = nameValue ? '' : this.strings.required;
        let emailError = '';
        if (!emailValue) emailError = this.strings.required;
        else if (!EMAIL_PATTERN.test(emailValue)) emailError = this.strings.invalidEmail;

        this.setFieldError(name, nameError);
        this.setFieldError(email, emailError);
        if (nameError || emailError) {
          (nameError ? name : email).focus();
          return;
        }

        this.lead = {
          full_name: nameValue,
          email: emailValue,
          accept_marketing: form.elements.accept_marketing.checked,
        };
        this.honeypot = form.elements.website ? form.elements.website.value : '';

        this.send('lead', this.lead);
        if (window.SkinQuizSavedResult) window.SkinQuizSavedResult.clear(this.storage());
        this.showStep(1);
      });

      form.querySelectorAll('.sq-field__input').forEach((input) => {
        input.addEventListener('input', () => this.setFieldError(input, ''));
      });
    }

    setFieldError(input, message) {
      const error = input.closest('.sq-field').querySelector('.sq-field__error');
      error.textContent = message || '';
      error.hidden = !message;
      input.classList.toggle('is-invalid', Boolean(message));
      input.setAttribute('aria-invalid', message ? 'true' : 'false');
    }

    // ------------------------------------------------------------ steps 1–4

    bindQuestions() {
      this.steps
        .filter((step) => step.dataset.question)
        .forEach((step) => {
          const error = step.querySelector('.sq-error');

          step.addEventListener('change', (event) => {
            if (event.target.matches('.sq-option__input')) error.hidden = true;
          });

          step.querySelector('.sq-next').addEventListener('click', () => {
            const selected = step.querySelector('.sq-option__input:checked');
            if (!selected) {
              error.textContent = this.strings.chooseOption;
              error.hidden = false;
              return;
            }

            this.answers[step.dataset.question] = this.answerFrom(selected);

            const number = Number(step.dataset.step);
            if (number < LAST_QUESTION_STEP) {
              this.showStep(number + 1);
            } else {
              this.finish();
            }
          });

          step.querySelectorAll('[data-goto]').forEach((button) => {
            button.addEventListener('click', () => this.showStep(Number(button.dataset.goto)));
          });
        });
    }

    bindTooltips() {
      this.addEventListener('click', (event) => {
        const opener = event.target.closest('.sq-option__info');
        if (opener) {
          this.openTooltip(document.getElementById(opener.getAttribute('aria-controls')), opener);
          return;
        }
        const tooltip = event.target.closest('.sq-tooltip');
        if (tooltip && (event.target.closest('.sq-tooltip__close') || !event.target.closest('.sq-tooltip__panel'))) {
          this.closeTooltip();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.openTooltipElement) this.closeTooltip();
      });
    }

    openTooltip(tooltip, opener) {
      if (!tooltip) return;
      this.closeTooltip();
      tooltip.hidden = false;
      this.openTooltipElement = tooltip;
      this.tooltipOpener = opener;
      tooltip.querySelector('.sq-tooltip__close').focus();
    }

    closeTooltip() {
      if (!this.openTooltipElement) return;
      this.openTooltipElement.hidden = true;
      this.openTooltipElement = null;
      if (this.tooltipOpener) this.tooltipOpener.focus();
      this.tooltipOpener = null;
    }

    // ------------------------------------------------------------ finish

    finish() {
      const startedAt = Date.now();
      this.steps.forEach((step) => {
        step.hidden = true;
      });
      this.loading.hidden = false;
      this.scrollIntoViewIfNeeded();

      const match = this.matchAnswers();

      const productIds = match
        ? Array.from(new Set(STAGES.flatMap((stage) => match.scenario[stage] || []))).map(String)
        : [];

      this.send('entry', {
        ...this.lead,
        answers: Object.fromEntries(
          Object.entries(this.answers).map(([question, answer]) => [question, { code: answer.code, label: answer.label }])
        ),
        matched: Boolean(match),
        matched_scenario: match ? match.scenario.handle : null,
        matched_specificity: match ? match.specificity : null,
        recommended_product_ids: productIds,
      });

      this.track('skin_quiz_complete', {
        skin_quiz_age: this.answers.age.code,
        skin_quiz_skin_type: this.answers.skin_type.code,
        skin_quiz_concern: this.answers.concern.code,
        skin_quiz_water: this.answers.water.code,
        skin_quiz_matched: Boolean(match),
      });

      if (window.SkinQuizSavedResult) {
        window.SkinQuizSavedResult.save(this.storage(), {
          name: this.lead ? this.lead.full_name : '',
          answers: Object.fromEntries(Object.entries(this.answers).map(([question, answer]) => [question, answer.code])),
        });
      }

      const wait = Math.max(0, (Number(this.config.loadingMinMs) || 0) - (Date.now() - startedAt));
      setTimeout(() => this.renderResult(match), wait);
    }

    matchAnswers() {
      return window.SkinQuizMatcher
        ? window.SkinQuizMatcher.match(this.scenarios, {
            age: this.answers.age.code,
            skin_type: this.answers.skin_type.code,
            concern: this.answers.concern.code,
          })
        : null;
    }

    answerFrom(input) {
      return {
        code: input.value,
        label: input.dataset.label,
        tipTitle: input.dataset.tipTitle || '',
        tipText: input.dataset.tipText || '',
      };
    }

    /**
     * Shows the saved result again on a reload or back/forward in this tab
     * (WordPress re-POSTs /skin-quiz-final/). Any other visit starts fresh.
     * Nothing is sent to the app, so no duplicate entry is recorded.
     */
    restoreResult() {
      const saved = window.SkinQuizSavedResult;
      if (!saved) return false;

      const storage = this.storage();
      const navigation = window.performance && performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
      if ((window.Shopify && window.Shopify.designMode) || !saved.isReturnVisit(navigation)) {
        saved.clear(storage);
        return false;
      }

      const result = saved.load(storage);
      if (!result) return false;

      const answers = {};
      for (const [question, code] of Object.entries(result.answers)) {
        const input = Array.from(this.querySelectorAll(`.sq-step[data-question="${question}"] .sq-option__input`)).find(
          (option) => option.value === code
        );
        // The answer was removed in admin since: start fresh.
        if (!input) {
          saved.clear(storage);
          return false;
        }
        input.checked = true;
        answers[question] = this.answerFrom(input);
      }

      this.answers = answers;
      this.lead = { full_name: result.name };
      this.steps.forEach((step) => {
        step.hidden = true;
      });
      this.renderResult(this.matchAnswers());
      return true;
    }

    renderResult(match) {
      const title = this.result.querySelector('.sq-result__title');
      title.textContent = (title.dataset.template || '').replace('{full_name}', this.lead ? this.lead.full_name : '').trim();

      let productCount = 0;
      STAGES.forEach((stage) => {
        const section = this.result.querySelector(`.sq-stage[data-stage="${stage}"]`);
        const items = section.querySelector('.sq-stage__items');
        items.replaceChildren();

        (match ? match.scenario[stage] || [] : []).forEach((productId, index) => {
          const template = this.querySelector(`template[data-product-template="${productId}"]`);
          if (!template) return;
          const fragment = template.content.cloneNode(true);
          fragment.querySelectorAll('form[id]').forEach((form) => {
            form.id = `${form.id}-${stage}-${index}`;
          });
          items.appendChild(fragment);
          productCount += 1;
        });

        section.hidden = items.children.length === 0;
      });

      this.result.querySelector('.sq-result__empty').hidden = productCount > 0;

      const addAll = this.result.querySelector('.sq-addall');
      addAll.hidden = !this.result.querySelector('.sq-product[data-available="true"]');
      addAll.querySelector('.sq-addall__message').hidden = true;

      const tip = this.result.querySelector('.sq-tip');
      const water = this.answers.water || {};
      tip.querySelector('.sq-tip__title').textContent = water.tipTitle || '';
      tip.querySelector('.sq-tip__text').textContent = water.tipText || '';
      tip.hidden = !water.tipTitle && !water.tipText;

      this.loading.hidden = true;
      this.result.hidden = false;
      this.result.querySelectorAll('.sq-product').forEach((product) => this.initReadMore(product));
      this.scrollIntoViewIfNeeded();
      title.focus({ preventScroll: true });
    }

    // ------------------------------------------------------------ result

    bindResult() {
      this.result.querySelector('.sq-result__down').addEventListener('click', () => {
        const firstStage = this.result.querySelector('.sq-stage:not([hidden])');
        if (firstStage) firstStage.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      this.result.querySelector('.sq-addall__button').addEventListener('click', (event) => {
        this.addAll(event.currentTarget);
      });

      this.result.addEventListener('click', (event) => {
        const toggle = event.target.closest('.sq-product__more');
        if (toggle) this.toggleReadMore(toggle);
      });
    }

    initReadMore(product) {
      const description = product.querySelector('[data-readmore]');
      const toggle = product.querySelector('.sq-product__more');
      if (!description || !toggle) return;

      const collapsed = window.matchMedia('(min-width: 750px)').matches ? READ_MORE_HEIGHT.desktop : READ_MORE_HEIGHT.mobile;
      if (description.scrollHeight <= collapsed + 16) return;

      description.style.setProperty('--sq-collapsed', `${collapsed}px`);
      description.classList.add('is-collapsed');
      toggle.hidden = false;
    }

    toggleReadMore(toggle) {
      const description = toggle.previousElementSibling;
      const label = toggle.querySelector('.sq-product__more-label');
      const expanded = toggle.getAttribute('aria-expanded') !== 'true';

      description.classList.toggle('is-collapsed', !expanded);
      toggle.setAttribute('aria-expanded', String(expanded));
      label.textContent = expanded ? label.dataset.less : label.dataset.more;
    }

    async addAll(button) {
      const message = this.result.querySelector('.sq-addall__message');
      const products = Array.from(this.result.querySelectorAll('.sq-stage:not([hidden]) .sq-product'));
      const seen = new Set();
      const items = [];
      const skipped = [];

      products.forEach((product) => {
        if (product.dataset.available !== 'true') {
          if (!skipped.includes(product.dataset.title)) skipped.push(product.dataset.title);
          return;
        }
        if (seen.has(product.dataset.variantId)) return;
        seen.add(product.dataset.variantId);

        const item = { id: Number(product.dataset.variantId), quantity: 1 };
        if (product.dataset.pricePerMl) item.properties = { price_per_ml: product.dataset.pricePerMl };
        items.push(item);
      });

      if (!items.length) return;

      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const spinner = button.querySelector('.loading-overlay__spinner');
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      spinner.classList.remove('hidden');
      message.hidden = true;

      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const body = { items };
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          this.showAddAllMessage(data.description || data.message || this.strings.addAllError, true);
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'skin-quiz' });
        }

        let text = this.strings.addedAll;
        if (skipped.length) text += ` ${this.strings.addAllSkipped} ${skipped.join(', ')}`;
        this.showAddAllMessage(text, false);
        this.track('skin_quiz_add_all', { skin_quiz_items: items.length });

        if (cart && typeof cart.renderContents === 'function') {
          cart.classList.remove('is-empty');
          cart.renderContents(data);
        } else {
          window.location = `${root}cart`;
        }
      } catch (error) {
        console.error('[skin-quiz] add all failed', error);
        this.showAddAllMessage(this.strings.addAllError, true);
      } finally {
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        spinner.classList.add('hidden');
      }
    }

    showAddAllMessage(text, isError) {
      const message = this.result.querySelector('.sq-addall__message');
      message.textContent = text;
      message.classList.toggle('is-error', isError);
      message.hidden = false;
    }

    // ------------------------------------------------------------ helpers

    /** sessionStorage, or null when the browser blocks it. */
    storage() {
      try {
        return window.sessionStorage;
      } catch (error) {
        return null;
      }
    }

    /** Fire-and-forget POST to the Matat app (app proxy). Never blocks the quiz. */
    send(kind, payload) {
      if (!this.config.leadCapture || !this.config.proxyPath) return;

      try {
        fetch(`${this.config.proxyPath.replace(/\/$/, '')}/${kind}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...payload, website: this.honeypot || '' }),
          keepalive: true,
        }).catch(() => {});
      } catch (error) {
        // Lead capture must never break the quiz.
      }
    }

    track(event, data = {}) {
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event, ...data });
    }
  }

  customElements.define('skin-quiz', SkinQuiz);
})();
